import { GsFormIfArg, GsFormSchema } from '../../../components/gs-form/interfaces/gs-form-schema';
import { GsStructBorderRadius, GsStructBorderStyle, GsStructBorderWidth } from '../../../models';
import { HORIZONTAL_ALIGNMENT_SCHEMA } from './alignment.schema';
import { BASIC_SCHEMA_FIELDS } from './../basics/basic.schema';
import { borderSchemaFields } from './../basics/border.schema';
import { buttonSchemaFields } from './../basics/button-fields.schema';
import { DEFAULT_CONFIG } from '../../../services/config/default.config';
import { colorBackgroundGeneratorSchema, colorTextGeneratorSchema } from './../basics/colorSchema.schema';
import { SPACING_SCHEMA } from './spacing.schema';

const IfButtonActiveArg: GsFormIfArg = {
  formControlName: 'showItemsButton',
  value: true,
  operator: '=='
};

const IfButtonInactiveArg: GsFormIfArg = {
  formControlName: 'showItemsButton',
  value: false,
  operator: '=='
};

/**
 * The schema for the Dashboard (My Account) component form.
 * Defines the fields (settings) available to configure the display of items/tiles
 * and their associated buttons in the dashboard.
 */
export const DASHBOARD_COMPONENT_FORM_SCHEMA: GsFormSchema = {
  fields: [
    ...BASIC_SCHEMA_FIELDS,
    ...HORIZONTAL_ALIGNMENT_SCHEMA,
    ...SPACING_SCHEMA,
    {
      formControlName: 'itemsRowDesktop',
      label: 'schema.items.itemsRowDesktop',
      type: 'number',
      min: 0,
      groupingName: 'components.dashboard.schema.items',
      default: 3
    },
    {
      formControlName: 'itemsRowTablet',
      label: 'schema.items.itemsRowTablet',
      type: 'number',
      min: 0,
      groupingName: 'components.dashboard.schema.items',
      default: 2
    },
    {
      formControlName: 'itemsRowMobile',
      label: 'schema.items.itemsRowMobile',
      type: 'number',
      min: 0,
      groupingName: 'components.dashboard.schema.items',
      default: 1
    },
    ...colorBackgroundGeneratorSchema(
      'item',
      'components.dashboard.schema.bgColor',
      'light-main',
      undefined,
      'components.dashboard.schema.items'
    ),
    ...colorTextGeneratorSchema(
      'item',
      'components.dashboard.schema.textColor',
      'dark-main',
      undefined,
      'components.dashboard.schema.items'
    ),
    ...borderSchemaFields(
      'Item',
      {
        borderColor: 'medium-main',
        borderWidth: GsStructBorderWidth.onePixel,
        borderStyle: GsStructBorderStyle.solid,
        borderRadius: GsStructBorderRadius.none
      },
      'components.dashboard.schema.items'
    ),
    {
      formControlName: 'showItemsDescription',
      label: 'components.dashboard.schema.showDescription',
      description: 'components.dashboard.schema.showDescriptionDescription',
      groupingName: 'components.dashboard.schema.items',
      type: 'checkbox',
      default: true
    },
    {
      formControlName: 'shortenItemsDescription',
      label: 'components.dashboard.schema.shortenDescription',
      description: 'components.dashboard.schema.shortenDescriptionDescription',
      groupingName: 'components.dashboard.schema.items',
      type: 'checkbox',
      default: true,
      if: {
        args: [
          {
            formControlName: 'showItemsDescription',
            value: true,
            operator: '=='
          }
        ]
      }
    },
    {
      formControlName: 'showItemsIcon',
      label: 'components.dashboard.schema.showIcon',
      description: 'components.dashboard.schema.showIconDescription',
      groupingName: 'components.dashboard.schema.items',
      type: 'checkbox',
      default: true
    },
    {
      formControlName: 'itemIconColor',
      label: 'components.dashboard.schema.iconColor',
      type: 'color',
      groupingName: 'components.dashboard.schema.items',
      default: 'secondary-main',
      if: {
        args: [
          {
            formControlName: 'showItemsIcon',
            value: true,
            operator: '=='
          }
        ]
      }
    },
    {
      formControlName: 'showItemsButton',
      label: 'components.dashboard.schema.showButton',
      groupingName: 'components.dashboard.schema.itemsButton',
      type: 'checkbox',
      default: false
    },
    {
      formControlName: 'showCustomButtonText',
      label: 'components.dashboard.schema.showCustomButtonText',
      groupingName: 'components.dashboard.schema.itemsButton',
      type: 'checkbox',
      default: false,
      if: { args: [IfButtonActiveArg] }
    },
    {
      formControlName: 'itemCustomButtonText',
      label: 'components.dashboard.schema.itemCustomButtonText',
      groupingName: 'components.dashboard.schema.itemsButton',
      type: 'text',
      default: '',
      placeholder: 'components.dashboard.schema.itemCustomButtonTextPlaceholder',
      if: { args: [IfButtonActiveArg, { formControlName: 'showCustomButtonText', value: true, operator: '==' }] }
    },
    ...colorTextGeneratorSchema(
      'itemHover',
      'components.dashboard.schema.itemHoverText',
      'dark-main',
      { args: [IfButtonInactiveArg] },
      'components.dashboard.schema.itemsButton'
    ),
    ...colorBackgroundGeneratorSchema(
      'itemHover',
      'components.dashboard.schema.itemHoverBackground',
      'light-shade',
      { args: [IfButtonInactiveArg] },
      'components.dashboard.schema.itemsButton'
    ),
    {
      formControlName: 'buttons',
      label: 'components.dashboard.schema.buttons',
      type: 'group',
      expandable: true,
      default: DEFAULT_CONFIG.uiElements?.buttons ?? {},
      fields: buttonSchemaFields()
    }
  ]
};
